import moment from "moment";

import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { MedicalRequestType } from "@/Common/Models/MedicalRequest/Enum/MedicalRequestType";
import { PortfolioExamRequestSortOrderType } from "@/Common/Models/UI/Enums";
import { ExamRequestPriorityOption } from "@/Common/Models/UI/Inputs/DropDownOption";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExportableFileDispatcher } from "@/ExportableFile/Helpers/ExportableFileDispatcher";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { MyRequestsTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { ILabRequestsInboxModel } from "@/Portfolio/Models/UI/IRequestsInboxModel";
import { MedicalRequestPopupController } from "./MedicalRequestForm/MedicalRequestPopupController";
import { MedicalRequestSettingsContainer } from "./MedicalRequestSettingsContainer";
import { PortfolioSaveOptionKey } from "./PortfolioSaveOptionKey";

interface IExamRequestsInboxProps {
    model: ILabRequestsInboxModel;
}

class ExamRequestsInboxState {
    public labRequestPinValid: boolean;
    public labRequestFaxValid: boolean;

    public genericFaxInfoName: string;
    public genericFaxInfoFax: string;

    public selectedExamRequestId: string;

    public static getDefault(): ExamRequestsInboxState {
        const s = new ExamRequestsInboxState();
        s.genericFaxInfoName = "";
        s.genericFaxInfoFax = "";
        s.labRequestFaxValid = false;
        s.labRequestPinValid = false;
        s.selectedExamRequestId = "";
        return s;
    }
}

class ExamRequestsInbox extends BaseReactComponent<IExamRequestsInboxProps, ExamRequestsInboxState> {
    private helper = sessionStore.helper;

    public constructor(props: IExamRequestsInboxProps) {
        super(props);
        this.state = ExamRequestsInboxState.getDefault();
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public render(): JSX.Element {
        const saveOptionKey = PortfolioSaveOptionKey.Request;

        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const cfgm = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="workspace">
                {this.state.selectedExamRequestId && (
                    <MedicalRequestPopupController
                        type={MedicalRequestType.LabRequest}
                        id={this.state.selectedExamRequestId}
                        showPrintHeader={true}
                        closeAfterButtonClick={true}
                        isDisplayAsReadonly={!cacheStore.ClinicSettings.EnableConsultationRequestWorkflowImprovements}
                        onClose={() => this.mutateState(s => s.selectedExamRequestId = null)}
                        onPrintSuccess={async exportableFile => PrintHelper.ShowPrintPreviewFileName((await exportableFile.getFetchUrlDtoAsync())?.FetchUrl)}
                        onSignClick={(model, printInfo) => {
                            this.mutateState(
                                s => s.selectedExamRequestId = null,
                                () => {
                                    ExportableFileDispatcher.openSignFaxPrintPopupForExamRequest({
                                        patientContactId: printInfo.patientId,
                                        requestId: model.id,
                                        requestDate: model.requestDate,
                                        exportMode: ExportMode.Sign,
                                        isPortalSyncEnabled: false,
                                        signedPrintAuthorizations: model.signedPrintAuthorizations,
                                        requestingStaffContactFacetId: model.requestingStaffContactFacetId
                                    });
                                }
                            );
                        }}
                        patientContactIdForNewRequests={null}
                        onSaveSuccess={() => sessionStore.dispatch(LocalOperations.Portfolio.MyRequests_SearchBoxTextChanged)}
                    />
                )}
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("MyExamRequestsLabel")}
                    searchText={this.props.model.get("GlobalPortfolioSearchText")}
                    searchPlaceholder={this.props.model.get("GlobalPortfolioSearchPlaceholder")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.Portfolio.MyRequests_SearchBoxTextChanged, { value: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Portfolio.MyRequests_SearchBoxTextChanged)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Portfolio.MyRequests_SearchSettings_ShowOptionsButtonClick)}
                />
                <div id="posref" className="ws-scrollable">
                    {this.props.model.get("ShowMyRequestsDisplaySettings")
                        && (
                            <MedicalRequestSettingsContainer
                                className="medical-request-settings"
                                footerClassName="medical-setting-footer"
                                type={MedicalRequestType.LabRequest}
                                model={this.props.model}
                                onCloseOp={LocalOperations.Portfolio.MyRequests_SearchSettings_CancelClick}
                                onOkOp={LocalOperations.Portfolio.MyRequests_SearchSettings_OkClick}
                                onColumnOrderingButtonClickOp={LocalOperations.Portfolio.MyRequests_SearchSettings_ColumnOrderingPopup_Open}
                                onResultsPerPageChangeOp={LocalOperations.Portfolio.MyRequests_SearchSettings_OnResultsPerPageChange}
                            />
                        )
                    }
                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="ExamRequestsInbox_main_tb"
                            Columns={tableColumns}
                            DataSource={this.props.model.get("TableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickFunction={data => !data.Model.IsAccessProhibited ? this.mutateState(s => s.selectedExamRequestId = data.Model.RequestId) : null}
                            ColumnSortTarget={MyleTableTarget.Myle_PortfolioMyRequests_Search_ColumnSortClick}
                            SaveOptionskeyTarget={saveOptionKey}
                            IsRowLocked={(item: any) => item.Model.IsAccessProhibited}
                            TableDataCy="portfolio-table"
                        />
                    </div>
                </div>
                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.props.model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.model.get("TotalQueryRows")}
                                CurrentPage={this.props.model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Portfolio.MyRequests_Search_CurrentPageChange}
                            />
                        </div>
                    </div>
                </div>

                {this.props.model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.Portfolio.MyRequests_SearchSettings_ColumnOrderingPopup_Close}
                    />
                )}
            </div>
        );
    }

    private labels = {
        Patient: "PatientLabel",
        Reason: "ReasonLabel",
        RequestingStaff: "RequestingStaffLabel",
        CreatedBy: "CreatedByLabel",
        CreatedOn: "CreationDateLabel",
        DueDate: "DueDateLabel",
        Priority: "PriorityLabel",
        Status: "StatusLabel"
    };

    private fieldNames = {
        Patient: "RequestsPatient",
        Reason: "Specialization",
        RequestingStaff: "RequestingStaff",
        CreatedBy: "CreatedBy",
        CreatedOn: "CreatedOn",
        DueDate: "DueDate",
        Priority: "Priority",
        Status: "Status"
    };

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Patient),
                FieldName: this.fieldNames.Patient,
                WidthPercentageDefault: 10,
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: PortfolioExamRequestSortOrderType.Patient,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioExamRequestSortOrderType.Patient
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Reason),
                FieldName: this.fieldNames.Reason,
                WidthPercentageDefault: 10,
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: PortfolioExamRequestSortOrderType.Reason,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioExamRequestSortOrderType.Reason
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.RequestingStaff),
                FieldName: this.fieldNames.RequestingStaff,
                WidthPercentageDefault: 10,
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: PortfolioExamRequestSortOrderType.RequestingStaff,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioExamRequestSortOrderType.RequestingStaff
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.CreatedBy),
                FieldName: this.fieldNames.CreatedBy,
                WidthPercentageDefault: 10,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.CreatedOn),
                FieldName: this.fieldNames.CreatedOn,
                WidthPercentageDefault: 10,
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: PortfolioExamRequestSortOrderType.CreatedOn,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioExamRequestSortOrderType.CreatedOn
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.DueDate),
                FieldName: this.fieldNames.DueDate,
                WidthPercentageDefault: 10,
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: PortfolioExamRequestSortOrderType.DueDate,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioExamRequestSortOrderType.DueDate
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Priority),
                FieldName: this.fieldNames.Priority,
                WidthPercentageDefault: 8,
                RowClickEnabled: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Status),
                FieldName: this.fieldNames.Status,
                WidthPercentageDefault: 8,
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: PortfolioExamRequestSortOrderType.Status,
                SortDirection: this.props.model.get("OrderByDirection"),
                IsSortActive: this.props.model.get("OrderByColumn") === PortfolioExamRequestSortOrderType.Status

            })
        ];
    }

    private getTableColumns(tableOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        const blockedLabel = cacheStore.GetString("Allowlist_BlockedLabel");
        return MyleTableColumn.buildList<MyRequestsTableItem>(
            tableOptions,
            column => {
                switch (column.FieldName) {
                    case this.fieldNames.Patient:
                        return {
                            renderCell: item => this.getRowRender(
                                this.isUrgent(item),
                                item.Model.IsAccessProhibited
                                    ? <label>{blockedLabel}</label>
                                    : (
                                        <a onClick={e => this.onPatientClick(item, () => e.stopPropagation())}>
                                            {item.Model.PatientName}
                                        </a>
                                    )
                            )
                        };

                    case this.fieldNames.Reason:
                        return {
                            renderCell: item => {
                                const reason = IsNullOrWhiteSpace(item.Model.Reason) ? item.Model.Request : item.Model.Reason;
                                return this.getRowRender(
                                    this.isUrgent(item),
                                    <label style={{ textOverflow: "ellipsis", width: "253px", whiteSpace: "nowrap", overflow: "hidden" }}>
                                        {item.Model.IsAccessProhibited ? blockedLabel : reason.replace(/\n+/g, " ")}
                                    </label>
                                );
                            }
                        };

                    case this.fieldNames.RequestingStaff:
                        return {
                            renderCell: item => {
                                const requestingStaff = cacheStore.GetEmployeeByContactFacetId(item.Model.RequestingStaffContactFacetId);
                                const requestingStaffTitle = cacheStore.GetLookupById(requestingStaff.PrefixLookupId);

                                return this.getRowRender(
                                    this.isUrgent(item),
                                    <label>{`${requestingStaffTitle?.GetValue() ?? ""} ${requestingStaff.LastName}, ${requestingStaff.FirstName}`}</label>
                                );
                            }
                        };

                    case this.fieldNames.CreatedBy:
                        return {
                            renderCell: item => this.getRowRender(
                                this.isUrgent(item),
                                <label>{this.getCreatedByString(item.Model.CreatedByContactId)}</label>
                            )
                        };

                    case this.fieldNames.CreatedOn:
                        return {
                            renderCell: item => this.getRowRender(
                                this.isUrgent(item),
                                <label>{DateHelper.formatDate(item.Model.CreationDate, "YYYY/MM/DD")}</label>
                            )
                        };

                    case this.fieldNames.DueDate:
                        return {
                            renderCell: item => this.getRowRender(
                                this.isUrgent(item),
                                <label>{DateHelper.formatDate(item.Model.DueDate, "YYYY/MM/DD")}</label>
                            )
                        };

                    case this.fieldNames.Priority:
                        return {
                            renderCell: item => this.getRowRender(
                                this.isUrgent(item),
                                <label>{item.Model.IsAccessProhibited ? blockedLabel : this.getPriorityString(item.Model.PriorityDropdownId)}</label>
                            )
                        };

                    case this.fieldNames.Status:
                        return {
                            renderCell: item => this.getRowRender(
                                this.isUrgent(item),
                                <label>{item.Model.IsAccessProhibited ? blockedLabel : this.getStatusString(item.Model.StatusLookupId)}</label>
                            )
                        };
                }
            },
            false
        );
    }

    private isUrgent(item: MyRequestsTableItem): boolean {
        return this.isUrgentPriority(item.Model.PriorityDropdownId) || moment() > moment(item.Model.DueDate);
    }

    private getRowRender(isUrgent: boolean, divElement: JSX.Element): JSX.Element {
        const style = isUrgent ? { color: "red" } : null;
        return (
            <div style={style}>
                {divElement}
            </div>
        );
    }

    private onPatientClick(item: MyRequestsTableItem, then: () => void): void {
        sessionStore.dispatch(LocalOperations.Portfolio.MyRequests_OnPatientClick, { item: item });
        then();
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(p => p.IsHidden === false).map(p => p.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        });
    }

    private isUrgentPriority(priorityId: string): boolean {
        return priorityId === ExamRequestPriorityOption.Urgent.model.Id;
    }

    private getStatusString(status: string): string {
        const statusesLookUps = cacheStore.FindLookupsByTypeName("MedicalRequestStatus");
        return statusesLookUps.firstOrDefault(p => p.LookupID === status)?.GetValue() ?? "";
    }

    private getPriorityString(priorityId: string): string {
        const allPriorities = ExamRequestPriorityOption.getAll();
        return allPriorities.firstOrDefault(p => p.model.Id === priorityId)?.getName() ?? "";
    }

    private getCreatedByString(createdById: string): string {
        const employee = this.props.model.get("CreatedByGlobalCache")
            ?.toArray()
            .firstOrDefault(p => p.Id === createdById);

        return employee?.Name ?? "";
    }
}

export { ExamRequestsInbox };
